import type { TRunWithPayload } from 'pullstate';
import { Fragment, useCallback, useMemo } from 'react';

import useMe from '@/renderer/utils/useMe';
import { useNextInfiniteRequest } from './api/infiniteStore';

// const JITTER = 5 * 60 * 1000 * Math.random();
// const MANUAL_UPDATE_INTERVAL = 60 * 60 * 1000 + JITTER;

export const MEMBERSHIPS_QUERY = {
  page: {
    limit: 100,
  },
};

const EPOCH_SENTINELS = new Set(['1970-01-01T00:00:00.000Z', '1970-01-01T00:00:00.000000Z']);

function isExpired(membership: API.Membership<true>, now: number): boolean {
  const { endsAt } = membership;

  if (!endsAt || EPOCH_SENTINELS.has(endsAt)) {
    return false;
  }

  const end = Date.parse(endsAt);

  return Number.isFinite(end) && end <= now;
}

/**
 * Whether a membership's product can still be purchased. A discontinued product
 * (e.g. RAS+) is either retired at the product level (`available === false`),
 * past its end date, or keeps its rows but has every offer hidden/unavailable —
 * in which case `/order/<id>` dead-ends at "No Products Found". Gate on the
 * product's `available` / offer `hidden` flags so this generalizes to any future
 * discontinued product rather than hardcoding a specific one.
 */
export function isProductOfferedForSale(
  product: API.MembershipProduct | undefined,
  now: number = Date.now(),
): boolean {
  // Unknown product info: fail open (don't hide rows we can't classify).
  if (!product) {
    return true;
  }

  if (product.available === false) {
    return false;
  }

  if (product.endDate) {
    const end = Date.parse(product.endDate);

    if (Number.isFinite(end) && end <= now) {
      return false;
    }
  }

  // Still for sale only if at least one offer is actually purchasable (not
  // hidden and not explicitly unavailable). The server already drops products
  // that are unavailable at the product level, but a product can stay
  // `available: true` with every offer hidden — that's the RAS+ dead-end.
  if (Array.isArray(product.offers)) {
    return product.offers.some((offer) => !offer.hidden && offer.available !== false);
  }

  // No offers present in the payload: fall back to the model's computed buyable
  // stat (null/undefined => unknown => fail open).
  return product.stats?.buyable == null || product.stats.buyable > 0;
}

/**
 * Whether a product is permanently discontinued — retired at the product level
 * (`available === false`) or past its own `endDate` — as opposed to merely being
 * between purchasable offer windows. A discontinued product's membership should
 * drop out of "active services" even when it still has leftover plays and no
 * per-membership end date: that's the RAS+ / old-season case where the row has
 * `remaining > 0` and an epoch/blank `endsAt` that never reads as expired, so the
 * expired-and-unavailable filter alone lets it keep showing as "Active".
 *
 * Deliberately narrower than `isProductOfferedForSale`: we do NOT treat "every
 * offer currently hidden" as discontinued here, so a live product that's just
 * temporarily between offer windows keeps showing the member's remaining plays.
 */
export function isProductDiscontinued(
  product: API.MembershipProduct | undefined,
  now: number = Date.now(),
): boolean {
  // Unknown product info: fail open (don't hide rows we can't classify).
  if (!product) {
    return false;
  }

  if (product.available === false) {
    return true;
  }

  if (product.endDate) {
    const end = Date.parse(product.endDate);

    if (Number.isFinite(end) && end <= now) {
      return true;
    }
  }

  return false;
}

export function useMemberships({
  // If true, merges memberships with the same productId into one by adding remaining and merging bonuses
  mergeSame = false,
  // Temporary for testing in prod
  omitEmpty = false,
  // If true, drop expired memberships whose product is no longer offered for sale
  // (nothing to renew — the "Renew" CTA would dead-end at "No Products Found").
  hideExpiredUnavailable = false,
} = {}) {
  const me = useMe();
  const pause = !me;
  const meId = me?.id;

  const infinite = useNextInfiniteRequest<
    API.Membership<true>,
    API.PageInput,
    API.WithPageInput<{ bonus?: 'include' | 'separate' | 'omit'; expired?: 'buyable' | 'omit' }>
  >(
    useMemo(
      () => ({
        key: `memberships:${meId}`,
        req: `/api/v1/membership/me`,
        event: 'membership',
        pause,
        skipPoll: true,
        serverSkip: true,
        // Every consumer gates on isPending, so load beside the page instead of
        // suspending it (and everything mounted after it) until this lands.
        suspendOnClient: false,
        query: MEMBERSHIPS_QUERY,
      }),
      [meId, pause],
    ),
  );

  // Filter/merge synchronously during render (via useMemo) rather than in a
  // post-render effect. On a hard refresh the infinite store rehydrates the raw
  // membership list synchronously; if filtering ran in an effect the first paint
  // would show the full unfiltered set (remaining=0, expired, discontinued/test
  // products) and only settle after the effect ran, causing a visible flash.
  // Memoizing keeps the very first render already filtered.
  const filteredData = useMemo(() => {
    if (!(mergeSame || omitEmpty || hideExpiredUnavailable)) {
      return infinite.data;
    }

    if (!infinite.data?.data) {
      return infinite.data; // undefined or null
    }

    const now = Date.now();
    const newData: API.WithPage<API.Membership<true>[], void> = {
      ...infinite.data,
      data: [],
    };

    const ids: Map<string, API.Membership<true>> = new Map();
    const idxs: Map<string, number> = new Map();

    for (const membership of infinite.data.data) {
      if (omitEmpty && membership.remaining === 0) {
        continue;
      }

      // Drop memberships that shouldn't read as an active service:
      //  - discontinued products, even with leftover plays and no per-row end
      //    date (RAS+ / old-season rows that never look "expired"); or
      //  - expired rows whose product is no longer offered for sale, so the
      //    "Renew" CTA would dead-end at "No Products Found".
      if (
        hideExpiredUnavailable &&
        (isProductDiscontinued(membership.product, now) ||
          (isExpired(membership, now) && !isProductOfferedForSale(membership.product, now)))
      ) {
        continue;
      }

      const key = `${membership.productId}:${membership.pauseUntil}`;
      const idx = idxs.get(key);
      const old = ids.get(key);

      if (mergeSame && idx !== undefined && old) {
        const next = {
          ...old,
          remaining: old.remaining + membership.remaining,
          bonuses:
            old.bonuses?.length || membership.bonuses?.length
              ? (old.bonuses || []).concat(membership.bonuses || [])
              : old.bonuses,
          pauseUntil:
            Date.parse(old.pauseUntil) > Date.parse(membership.pauseUntil)
              ? old.pauseUntil
              : membership.pauseUntil,
        };

        ids.set(key, next);
        newData.data[idx] = next;
      } else {
        idxs.set(key, newData.data.push(membership) - 1);
        ids.set(key, membership);
      }
    }

    return newData;
  }, [infinite.data, mergeSame, omitEmpty, hideExpiredUnavailable]);

  const renderPayload: TRunWithPayload<API.WithPage<API.Membership<true>[]>> = useCallback(
    (func) => {
      if (!infinite.error && filteredData) {
        return func(filteredData);
      }

      return Fragment;
    },
    [infinite.error, filteredData],
  );

  /// @todo polling, and removing memberships when remaining = 0

  if (mergeSame || omitEmpty || hideExpiredUnavailable) {
    return new Proxy<typeof infinite>(infinite, {
      get(target, prop) {
        if (prop === 'data') {
          return filteredData;
        } else if (prop === 'renderPayload') {
          return renderPayload;
        }

        return Reflect.get(target, prop);
      },
    });
  }

  return infinite;
}
